import React from "react";
import { Form, Image, Badge } from "react-bootstrap";

const AVATAR_SIZE = 40;

// Single row in the modal's results list (semantic / by_name) or in a
// grouped section (by_outlet). Renders checkbox + avatar + name + small
// metadata + relevance/match badge. Click anywhere except the checkbox
// to focus the row in the detail pane.
//
// `alreadyAdded` rows show an "Already added" badge and a disabled
// checkbox — the admin can still click to focus the row in the detail
// pane, but can't (re)select it. The modal's add-selected pipeline also
// filters these out defensively.
export default function ResultRow({ candidate, checked, onToggleCheck, onFocus, isFocused, alreadyAdded = false }) {
  const {
    id,
    name,
    workplace,
    title,
    semantic_distance: semanticDistance,
    match_type: matchType,
    profile_image_url: profileImageUrl,
  } = candidate;

  const distanceLabel = semanticDistance != null ? semanticDistance.toFixed(2) : null;

  return (
    <div
      className={`d-flex align-items-center gap-2 px-3 py-2 border-bottom ${isFocused ? "bg-light" : ""}`}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        // Skip focus toggle when the click came from the checkbox itself —
        // the checkbox onChange handles that.
        if (e.target.tagName === "INPUT") return;
        onFocus(candidate);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          if (e.target.tagName !== "INPUT") {
            e.preventDefault();
            onFocus(candidate);
          }
        }
      }}
      style={alreadyAdded ? { opacity: 0.6 } : undefined}
    >
      <Form.Check
        type="checkbox"
        checked={alreadyAdded ? true : checked}
        disabled={alreadyAdded}
        onChange={() => onToggleCheck(id)}
        onClick={(e) => e.stopPropagation()}
        aria-label={alreadyAdded ? `${name} (already on list)` : `Select ${name}`}
      />
      <Image
        roundedCircle
        src={profileImageUrl}
        alt=""
        style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, objectFit: "cover", flexShrink: 0 }}
      />
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="fw-semibold text-truncate">{name}</div>
        <div className="text-muted small text-truncate">
          {title && <span>{title}</span>}
          {title && workplace && <span> · </span>}
          {workplace && <span>{workplace}</span>}
        </div>
      </div>
      <div className="text-end" style={{ minWidth: 60 }}>
        {alreadyAdded && (
          <Badge bg="secondary" pill style={{ fontSize: "0.65rem" }}>
            Added
          </Badge>
        )}
        {!alreadyAdded && matchType === "semantic" && distanceLabel && (
          <span className="text-muted" style={{ fontSize: "0.7rem" }} title="Semantic distance">
            {distanceLabel}
          </span>
        )}
        {!alreadyAdded && matchType === "by_name" && (
          <Badge bg="info" pill style={{ fontSize: "0.65rem" }}>
            Name
          </Badge>
        )}
      </div>
    </div>
  );
}
